import { History } from "lucide-react";
import { useEffect, useState } from "react";
import { listAudit } from "../services/api";
import type { AuditRecord } from "../types/peopleAdmin";

interface AuditPanelProps {
  targetUserId: string;
  refreshKey: number;
}

export default function AuditPanel({ targetUserId, refreshKey }: AuditPanelProps) {
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadAudit() {
      try {
        const result = await listAudit(targetUserId);
        if (mounted) {
          setRecords(result);
          setError(null);
        }
      } catch (loadError) {
        if (mounted) {
          setError(loadError instanceof Error ? loadError.message : "Could not load audit records.");
        }
      }
    }

    loadAudit();
    return () => {
      mounted = false;
    };
  }, [targetUserId, refreshKey]);

  return (
    <section className="audit-panel" aria-label="Audit log">
      <div className="inline-tool-heading">
        <History size={18} aria-hidden="true" />
        <h3>Audit</h3>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {!error && records.length === 0 ? <div className="loading-row">No recent changes.</div> : null}

      {records.length > 0 ? (
        <div className="audit-table-wrap">
          <table className="audit-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Fields</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={`${record.timestamp}-${record.action}-${record.result}`}>
                  <td>{new Date(record.timestamp).toLocaleString()}</td>
                  <td>{record.action === "UpdateUserPhoto" ? "Photo" : "Profile"}</td>
                  <td>{record.changedFields.join(", ")}</td>
                  <td>
                    <span className={`result-pill result-${record.result.toLocaleLowerCase()}`}>
                      {record.result}
                    </span>
                    {record.failureReason ? <span className="failure-reason">{record.failureReason}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
